import { PropsWithChildren, ReactElement, useMemo } from "react";
import * as CSS from "csstype";
import { NavLink } from "react-router-dom";

import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Link,
  Spacer,
  Stack,
  StackDirection,
  Text,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";

import { QuotationLineModelDocumentModelContractStatusEnum } from "api";
import { DesktopOnly, MobileOnly } from "components/BreakpointVisibility";
import Card, { CardBody, CardHeader, CardProps } from "components/Card";
import ClickToCopy from "components/ClickToCopy";
import ConditionCheck from "components/ConditionCheck";
import ContractQuantityProgressBarSection from "components/ContractQuantityProgressBarSection";
import ContractStat from "components/ContractStat";
import ContractStatus from "components/ContractStatus";
import PrimaryButton from "components/PrimaryButton";
import ShipStartEndWidget from "components/ShipStartEndWidget";
import TripWidget from "components/TripWidget";
import { OpenRequestOrderFormButton } from "forms/RequestOrderForm";
import useFeatureFlag from "hooks/useFeatureFlag";
import usePermission from "hooks/usePermission";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { iconSpacing } from "icons";
import { NO_MORE_ORDERS_THRESHOLD } from "util/constants";
import { ESIGN, ORDERS } from "util/featureFlags";
import { DEFAULT_EMPTY_VALUE } from "util/statHelpers";
import { mobileStyleBreakpoint } from "util/styleHelpers";

export type ContractCardProps = {
  contract: Contract;
};

type CustomerInfoProps = {
  customerName: string;
  customerId: string;
};

const contractNumberSize = mobileStyleBreakpoint<string>("sm", "lg");
export const statusValueTextStyle = mobileStyleBreakpoint<string>("body", "h3");
const statusAlignItems = mobileStyleBreakpoint<CSS.Property.AlignItems>(
  "flex-start",
  "center"
);
const headerSpacing = mobileStyleBreakpoint<CSS.Property.JustifyContent>(
  "flex-start",
  "space-between"
);
const marginBottom = mobileStyleBreakpoint(2, 0);
const paddingTop = mobileStyleBreakpoint(4, 0);

const DAYS_REMAINING_WIDTH = "164px";

const DESKTOP_PO_NUMBER_MAX_LENGTH = 15;

const DAYS_REMAINING_BADGE_THRESHOLD = 14;

const DaysRemainingBadge = ({
  daysRemaining,
}: {
  daysRemaining: number;
}): ReactElement => (
  <Flex maxW={DAYS_REMAINING_WIDTH}>
    <Badge
      w={DAYS_REMAINING_WIDTH}
      variant="solid"
      px={4}
      py={2}
      borderRadius="36px"
      colorScheme="red"
      bgColor="red.600"
      textTransform="none"
      textAlign="center"
    >
      <Text textStyle="body2" color="inherit">
        {daysRemaining < 0 ? 0 : daysRemaining}{" "}
        {daysRemaining === 1 ? "day" : "days"} remaining
      </Text>
    </Badge>
  </Flex>
);

const CustomerInfo = ({ customerName, customerId }: CustomerInfoProps) => (
  <Stack spacing={4} pt={2}>
    <Text color="grey.700" fontWeight="semibold">
      {customerName}
    </Text>
    <Text color="grey.700" fontWeight="semibold">
      {customerId}
    </Text>
  </Stack>
);

export const ContractCardHeader = ({
  customerName,
  customerId,
  contractNumber = DEFAULT_EMPTY_VALUE,
  status,
  poNumber,
  isUnsigned = false,
  isExpiringSoon = false,
  daysRemaining,
  canRequestMoreOrders,
  quantityRemainingToOrder,
}: {
  customerName: string;
  customerId: string;
  contractNumber: number | undefined | typeof DEFAULT_EMPTY_VALUE;
  status: string;
  poNumber: string;
  isUnsigned?: boolean;
  isExpiringSoon?: boolean;
  daysRemaining?: number;
  canRequestMoreOrders?: boolean;
  quantityRemainingToOrder: number;
}): ReactElement => {
  const size = useBreakpointValue(contractNumberSize);
  const textStyle = useBreakpointValue(statusValueTextStyle);

  const StatusLabel = useMemo(
    () => (
      <Flex alignItems={statusAlignItems} mb={marginBottom}>
        <ContractStatus
          size={size}
          status={status}
          isUnsigned={isUnsigned}
          textStyle={statusValueTextStyle}
        />
      </Flex>
    ),
    [size, status, isUnsigned]
  );

  const ContractNumberStat = useMemo(
    () => (
      <ContractStat label="Contract #" size={size}>
        <HStack spacing={iconSpacing} pb={2}>
          <Text textStyle={textStyle} fontWeight="black">
            {contractNumber}
          </Text>
          <DesktopOnly>
            <ClickToCopy value={contractNumber} label="Contract Number" />
          </DesktopOnly>
        </HStack>
      </ContractStat>
    ),
    [contractNumber, size, textStyle]
  );

  const displayDaysRemainingBadge =
    canRequestMoreOrders &&
    isExpiringSoon &&
    quantityRemainingToOrder >= NO_MORE_ORDERS_THRESHOLD &&
    (daysRemaining || daysRemaining === 0) &&
    daysRemaining <= DAYS_REMAINING_BADGE_THRESHOLD;

  return (
    <CardHeader>
      <Stack w="100%">
        <HStack justifyContent={headerSpacing} spacing={10}>
          <DesktopOnly>
            <HStack spacing={14}>
              {ContractNumberStat}
              <ContractStat label="PO #" size={size} w="250px" maxW="250px">
                <HStack spacing={iconSpacing}>
                  <Text
                    textStyle={textStyle}
                    fontWeight="black"
                    noOfLines={1}
                    title={poNumber}
                  >
                    {poNumber?.length > DESKTOP_PO_NUMBER_MAX_LENGTH + 3
                      ? `${poNumber.slice(0, DESKTOP_PO_NUMBER_MAX_LENGTH)}...`
                      : poNumber}
                  </Text>
                  {!!poNumber && poNumber !== DEFAULT_EMPTY_VALUE && (
                    <DesktopOnly>
                      <ClickToCopy value={poNumber} label="PO Number" />
                    </DesktopOnly>
                  )}
                </HStack>
              </ContractStat>
            </HStack>
          </DesktopOnly>
          <DesktopOnly>
            <>
              {StatusLabel}
              {displayDaysRemainingBadge ? (
                <DaysRemainingBadge daysRemaining={daysRemaining} />
              ) : (
                <Spacer w={DAYS_REMAINING_WIDTH} maxW={DAYS_REMAINING_WIDTH} />
              )}
            </>
          </DesktopOnly>
        </HStack>
        <MobileOnly>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="start"
            width="100%"
          >
            <VStack display="start" w="50%">
              {ContractNumberStat}
              {StatusLabel}
              <ContractStat label="PO #" size={size}>
                <Text
                  textStyle={textStyle}
                  fontWeight="black"
                  whiteSpace="normal"
                  overflowWrap="break-word"
                >
                  {poNumber}
                </Text>
              </ContractStat>
              <CustomerInfo
                customerName={customerName}
                customerId={customerId}
              />
            </VStack>
            {displayDaysRemainingBadge && (
              <DaysRemainingBadge daysRemaining={daysRemaining} />
            )}
          </Box>
        </MobileOnly>
      </Stack>
    </CardHeader>
  );
};

const customerInfoDisplay = mobileStyleBreakpoint<CSS.Property.Display>(
  "none",
  "inline"
);

const contractCardFooterDirection = mobileStyleBreakpoint<string>(
  "column",
  "row"
) as StackDirection;

const buttonStackDirection = mobileStyleBreakpoint<string>(
  "column",
  "row"
) as StackDirection;

export const ContractCardBody = ({
  docEntry,
  customerName,
  customerId,
  origins,
  destinations,
  productName,
  quantityTotal,
  quantityTotalFormatted,
  quantityShipped,
  quantityShippedFormatted,
  quantityRemainingToOrder,
  quantityOnOrderOpen,
  unitOfMeasure,
  onViewDetails,
  shipStartDate,
  shipEndDate,
  esignLink,
  esignButtonVisible = false,
  canRequestMoreOrders,
  status,
}: {
  customerName: string;
  customerId: string;
  onViewDetails?(): void;
} & Pick<
  Contract,
  | "docEntry"
  | "origins"
  | "destinations"
  | "productName"
  | "quantityTotal"
  | "quantityTotalFormatted"
  | "quantityShipped"
  | "quantityShippedFormatted"
  | "quantityRemainingToOrder"
  | "quantityOnOrderOpen"
  | "unitOfMeasure"
  | "shipStartDate"
  | "shipEndDate"
  | "esignLink"
  | "esignButtonVisible"
  | "canRequestMoreOrders"
  | "status"
>): ReactElement => {
  const selectedCustomerCode = useSelectedCustomerCode();
  const hasEsignFeatureFlag = useFeatureFlag(ESIGN.SIGN);
  const hasEsignPermission = usePermission(ESIGN.SIGN);

  const hasAddOrderPermission = usePermission(ORDERS.ADD);
  const hasAddOrderFlag = useFeatureFlag(ORDERS.ADD);
  const requestOrderConditions =
    hasAddOrderFlag &&
    hasAddOrderPermission &&
    canRequestMoreOrders &&
    status === QuotationLineModelDocumentModelContractStatusEnum.Open;
  return (
    <CardBody>
      <Stack w="100%" spacing={5} pt={paddingTop}>
        <HStack justify="space-between" align="flex-start">
          <Stack display={customerInfoDisplay} flex={1}>
            <Text fontWeight="semibold" color="grey.700" textStyle="body">
              {customerName}
            </Text>
            <HStack spacing={iconSpacing}>
              <Text fontWeight="semibold" color="grey.700" textStyle="body">
                {customerId}
              </Text>
              <DesktopOnly>
                <ClickToCopy value={customerId} label="Customer ID" />
              </DesktopOnly>
            </HStack>
          </Stack>
          <ShipStartEndWidget
            startDate={shipStartDate}
            endDate={shipEndDate}
            loaded={true}
            desktopRightAligned
          />
        </HStack>
        <ContractQuantityProgressBarSection
          productName={productName}
          quantityTotal={quantityTotal}
          quantityTotalFormatted={quantityTotalFormatted}
          quantityShippedFormatted={quantityShippedFormatted}
          quantityShipped={quantityShipped}
          onOrder={quantityOnOrderOpen}
          remaining={quantityRemainingToOrder}
          uom={unitOfMeasure}
        />
        <Divider />
        <Stack direction={contractCardFooterDirection}>
          <TripWidget origins={origins} destinations={destinations} />
          <Spacer />
          <Stack spacing={5} direction={buttonStackDirection}>
            <ConditionCheck
              conditions={[
                !!esignLink,
                esignButtonVisible,
                hasEsignPermission,
                hasEsignFeatureFlag,
              ]}
            >
              <Button as={Link} variant="outline" href={esignLink} isExternal>
                Sign Contract
              </Button>
            </ConditionCheck>
            <ConditionCheck conditions={[requestOrderConditions ?? false]}>
              <OpenRequestOrderFormButton
                as={NavLink}
                to={`/contracts?customer=${selectedCustomerCode}&docEntry=${docEntry}&tab=1&requestOrderFormOpen=true`}
                variant="outline"
              />
            </ConditionCheck>
            <PrimaryButton onClick={onViewDetails}>View Details</PrimaryButton>
          </Stack>
        </Stack>
      </Stack>
    </CardBody>
  );
};

const ContractCard = ({
  children,
  ...rest
}: PropsWithChildren<CardProps>): ReactElement => (
  <Card {...rest}>{children}</Card>
);

export const StandardContractCard = ({
  contract,
  onViewDetails,
  ...rest
}: ContractCardProps &
  CardProps & {
    onViewDetails?(docEntry: number): void;
  }): ReactElement => (
  <ContractCard {...rest}>
    <ContractCardHeader
      contractNumber={contract.contractNumber}
      status={contract.status}
      poNumber={contract.poNumber}
      isUnsigned={!!contract.esignButtonVisible}
      isExpiringSoon={contract.expiringSoon}
      daysRemaining={contract.daysRemaining}
      customerName={contract.customerName ?? "Name"}
      customerId={contract.customerCode ?? "ID"}
      canRequestMoreOrders={contract.canRequestMoreOrders}
      quantityRemainingToOrder={contract.quantityRemainingToOrder}
    />
    <ContractCardBody
      docEntry={contract.docEntry}
      customerName={contract.customerName ?? "Name"}
      customerId={contract.customerCode ?? "ID"}
      origins={contract.origins}
      destinations={contract.destinations}
      productName={contract.productName}
      quantityTotal={contract.quantityTotal}
      quantityTotalFormatted={contract.quantityTotalFormatted}
      quantityShipped={contract.quantityShipped}
      quantityShippedFormatted={contract.quantityShippedFormatted}
      quantityRemainingToOrder={contract.quantityRemainingToOrder}
      quantityOnOrderOpen={contract.quantityOnOrderOpen}
      unitOfMeasure={contract.unitOfMeasure}
      shipStartDate={contract.shipStartDate}
      shipEndDate={contract.shipEndDate}
      onViewDetails={() =>
        onViewDetails && contract.docEntry && onViewDetails(contract.docEntry)
      }
      esignLink={contract.esignLink}
      esignButtonVisible={contract.esignButtonVisible}
      canRequestMoreOrders={contract.canRequestMoreOrders}
      status={contract.status}
    />
  </ContractCard>
);

export default ContractCard;
